// DiscoveryTimeline — checkpoint event list, pure presentation.
//
// Renders the Redis-backed event log from a DiscoveryRun as a vertical
// list of checkpoint rows. Auto-scrolls to bottom on new events unless
// the user has scrolled up (sticky-scroll behavior — matches how
// terminal log viewers behave).
//
// Event types (per @/api/discovery DiscoveryEventType):
//   - checkpoint              — status text ("Probing 192.168.1.1")
//   - device_found            — info ("Found Hikvision DS-7716NI @ ...")
//   - device_added            — success ("✓ NVR added")
//   - device_existing         — neutral ("✓ existing, skipped")
//   - credential_needed       — placeholder; the CredentialPrompt is
//                                rendered separately by DiscoveryPanel
//   - credentials_resolved    — success ("✓ Credentials matched: admin")
//   - credentials_exhausted   — warning ("All candidates failed")
//   - cancel_requested        — neutral ("Cancel requested by user")
//   - error                   — error ("✗ Probe error: timeout")
//   - summary                 — terminal ("Done: 1 router, 1 NVR, 4 cameras")

import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  HelpCircle,
  Info,
  KeyRound,
  XCircle,
} from 'lucide-react';
import type { DiscoveryEvent, DiscoveryEventType } from '@/api/discovery';

export interface DiscoveryTimelineProps {
  events: DiscoveryEvent[];
  className?: string;
}

export function DiscoveryTimeline({ events, className }: DiscoveryTimelineProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const userScrolledRef = useRef(false);

  // Auto-scroll on new events unless user scrolled away from bottom.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (!userScrolledRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [events.length]);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 4;
    userScrolledRef.current = !atBottom;
  };

  if (events.length === 0) {
    return (
      <div className={cn('text-sm text-muted-foreground italic', className)}>
        …
      </div>
    );
  }

  return (
    <div
      ref={scrollerRef}
      onScroll={onScroll}
      className={cn(
        'max-h-64 space-y-1 overflow-y-auto rounded-md border border-border bg-background/50 p-2 font-mono text-xs',
        className,
      )}
    >
      {events.map((evt, i) => (
        <TimelineRow key={i} evt={evt} />
      ))}
    </div>
  );
}

function TimelineRow({ evt }: { evt: DiscoveryEvent }) {
  const Icon = iconFor(evt.type);
  const colorCls = colorFor(evt.type);
  const time = formatRelTime(evt.ts_ms);
  const text = renderText(evt);

  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 inline-flex w-10 shrink-0 tabular-nums text-muted-foreground">
        {time}
      </span>
      <Icon className={cn('mt-0.5 size-3.5 shrink-0', colorCls)} aria-hidden />
      <span className="flex-1 break-words text-foreground">{text}</span>
    </div>
  );
}

function iconFor(type: DiscoveryEventType) {
  switch (type) {
    case 'checkpoint': return Clock;
    case 'device_found': return Info;
    case 'device_added': return CheckCircle2;
    case 'device_existing': return Info;
    case 'credential_needed': return KeyRound;
    case 'credentials_resolved': return CheckCircle2;
    case 'credentials_exhausted': return AlertCircle;
    case 'cancel_requested': return XCircle;
    case 'error': return XCircle;
    case 'summary': return CheckCircle2;
    default: return HelpCircle;
  }
}

function colorFor(type: DiscoveryEventType): string {
  switch (type) {
    case 'device_added':
    case 'credentials_resolved':
    case 'summary':
      return 'text-emerald-600 dark:text-emerald-400';
    case 'credentials_exhausted':
      return 'text-amber-600 dark:text-amber-400';
    case 'error':
    case 'cancel_requested':
      return 'text-destructive';
    case 'credential_needed':
      return 'text-amber-600 dark:text-amber-400';
    default:
      return 'text-muted-foreground';
  }
}

// Lossy human-readable fallback. The orchestrator emits `text` for
// checkpoint events; other events get a synthesized line from their
// structured fields. i18n is intentionally light here — the
// orchestrator writes Russian text for the checkpoint payloads it
// authors, and the synthesized event lines below stay terse + bilingual
// (the dict still has `equipment.discovery.timeline.*` keys for the
// most common ones once we want full translation).
function renderText(evt: DiscoveryEvent): string {
  if (evt.text) return evt.text;
  const kind = evt.device_kind ? ` (${evt.device_kind})` : '';
  const ip = (evt.device?.identity?.ip) || '';
  switch (evt.type) {
    case 'device_found':
      return `Found${kind}${ip ? ` @ ${ip}` : ''}`;
    case 'device_added':
      return `Added${kind}${ip ? ` @ ${ip}` : ''}`;
    case 'device_existing':
      return evt.device_action === 'skipped'
        ? `Skipped${kind}${ip ? ` @ ${ip}` : ''}`
        : `Existing${kind}${ip ? ` @ ${ip}` : ''}`;
    case 'credential_needed':
      return `Awaiting credentials${kind}${ip ? ` @ ${ip}` : ''}`;
    case 'credentials_resolved': {
      const u = (evt.context as { matched_username?: string } | undefined)?.matched_username;
      return `Credentials matched${u ? `: ${u}` : ''}`;
    }
    case 'credentials_exhausted': {
      const n = (evt.context as { tried_count?: number } | undefined)?.tried_count;
      return n ? `All ${n} candidates failed` : 'All candidates failed';
    }
    case 'cancel_requested':
      return 'Cancel requested';
    case 'error':
      return `Error: ${evt.error_detail ?? 'unknown'}`;
    case 'summary':
      return 'Done';
    default:
      return evt.type;
  }
}

// "00:42" relative to first-event time-zero would require passing the
// start through; for now we use absolute-time-clock-style mm:ss based
// on the event's own ts_ms modulo the hour. Cheap and readable for
// runs under an hour.
function formatRelTime(ts_ms: number): string {
  const d = new Date(ts_ms);
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${mm}:${ss}`;
}
